import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Button,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';

// Components
import AlertCard from './AlertCard';
import AlertDetailModal from './AlertDetailModal';

const AlertsList = ({ alerts, onToggleAlert, onDeleteAlert, onCreateAlert, onClearInactiveAlerts }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Process alerts with filters
  const filteredAlerts = alerts.filter(alert => {
    // Search term filter
    if (searchTerm && !alert.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Type filter
    if (typeFilter !== 'all' && alert.type !== typeFilter) {
      return false;
    }

    // Status filter
    if (statusFilter === 'active' && !alert.enabled) {
      return false;
    }
    if (statusFilter === 'inactive' && alert.enabled) {
      return false;
    }

    return true;
  });

  // Get count of each alert type for the filter dropdown
  const getTypeCount = (type) => {
    if (type === 'all') return alerts.length;
    return alerts.filter(alert => alert.type === type).length;
  };

  // Get count of inactive alerts
  const inactiveCount = alerts.filter(alert => !alert.enabled).length;

  // Handle open clear dialog
  const handleOpenClearDialog = () => {
    setClearDialogOpen(true);
  };

  // Handle close clear dialog
  const handleCloseClearDialog = () => {
    setClearDialogOpen(false);
  };

  // Handle clear inactive alerts
  const handleClearInactiveAlerts = () => {
    onClearInactiveAlerts && onClearInactiveAlerts();
    setClearDialogOpen(false);
  };

  // Handle viewing alert details
  const handleViewDetails = (alert) => {
    setSelectedAlert(alert);
    setDetailModalOpen(true);
  };

  // Handle closing detail modal
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedAlert(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <TextField
          placeholder="Search alerts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="type-filter-label">Type</InputLabel>
            <Select
              labelId="type-filter-label"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Type"
              startAdornment={
                <FilterListIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }
            >
              <MenuItem value="all">All Types ({getTypeCount('all')})</MenuItem>
              <MenuItem value="priceAlert">Price Alerts ({getTypeCount('priceAlert')})</MenuItem>
              <MenuItem value="congressTrade">Congressional ({getTypeCount('congressTrade')})</MenuItem>
              <MenuItem value="newsAlert">News Alerts ({getTypeCount('newsAlert')})</MenuItem>
              <MenuItem value="insiderTrade">Insider Trading ({getTypeCount('insiderTrade')})</MenuItem>
              <MenuItem value="marketEvent">Market Events ({getTypeCount('marketEvent')})</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredAlerts.length} of {alerts.length} alerts
        </Typography>

        {inactiveCount > 0 && (
          <Tooltip title="Clear inactive alerts">
            <IconButton
              color="default"
              onClick={handleOpenClearDialog}
              sx={{
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <CleaningServicesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {alerts.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            You don't have any alerts yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first alert to start getting notified about market events that matter to you.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onCreateAlert}
            sx={{ mt: 2 }}
          >
            Create Your First Alert
          </Button>
        </Paper>
      ) : filteredAlerts.length === 0 ? (
        <Alert severity="info">
          No alerts match your current filters. Try adjusting your search or filters.
        </Alert>
      ) : (
        <Box>
          {filteredAlerts.map(alert => (
            <AlertCard
              key={alert._id}
              alert={alert}
              onToggle={onToggleAlert}
              onDelete={onDeleteAlert}
              onViewDetails={handleViewDetails}
            />
          ))}
        </Box>
      )}

      {/* Clear Inactive Alerts Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCloseClearDialog}
        aria-labelledby="clear-alerts-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(19, 47, 76, 0.8)'
              : 'rgba(255, 255, 255, 0.9)',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle id="clear-alerts-dialog-title">
          Clear inactive alerts
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you really want to remove all inactive alert records?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCloseClearDialog}
            variant="outlined"
            sx={{
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                bgcolor: 'transparent'
              }
            }}
          >
            No
          </Button>
          <Button
            onClick={handleClearInactiveAlerts}
            variant="contained"
            color="error"
            sx={{ ml: 1 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDetailModal
        alert={selectedAlert}
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
      />

    </Box>
  );
};

export default AlertsList;